"use client";

import { api } from "convex/_generated/api";
import { useConvexAuth, useMutation } from "convex/react";
import { useEffect, useRef } from "react";
import {
  ANALYTICS_EVENTS,
  identifyAnalyticsUser,
  track,
} from "@/lib/analyticsEvents";
import {
  clearSignupAttribution,
  readSignupAttribution,
} from "@/lib/signupAttribution";
import { toSignupAttributionEventProperties } from "../../shared/signupAttribution";

export const VISITOR_ID_STORAGE_KEY = "darsi_visitor_id";

export function EnsureUser({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const bootstrapUser = useMutation(api.users.bootstrapUser);
  const bootstrappingRef = useRef(false);
  const signupTrackedRef = useRef(false);
  const deviceBlockedTrackedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (bootstrappingRef.current) {
        return;
      }
      bootstrappingRef.current = true;
      void (async () => {
        const captureBootstrap = (result: Awaited<ReturnType<typeof bootstrapUser>>) => {
          if (result.isNewUser && !signupTrackedRef.current) {
            signupTrackedRef.current = true;
            identifyAnalyticsUser(result.user.clerkId);
            track(ANALYTICS_EVENTS.signupCompleted, {
              isNewUser: result.isNewUser,
              trialStarted: result.trialStarted,
              deviceTrialBlocked: result.deviceTrialBlocked,
              ...toSignupAttributionEventProperties(result.user),
            });
          }
          if (result.deviceTrialBlocked && !deviceBlockedTrackedRef.current) {
            deviceBlockedTrackedRef.current = true;
            track(ANALYTICS_EVENTS.deviceTrialBlocked);
          }
          clearSignupAttribution();
        };
        const signupAttribution = readSignupAttribution();
        try {
          const fpModule = await import("@fingerprintjs/fingerprintjs");
          const fpAgent = await fpModule.load();
          const fpResult = await fpAgent.get();
          const visitorId = fpResult.visitorId?.trim();
          if (visitorId) {
            localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
          }
          const result = await bootstrapUser({
            visitorId,
            ...signupAttribution,
          });
          captureBootstrap(result);
        } catch {
          const result = await bootstrapUser(signupAttribution);
          captureBootstrap(result);
        }
      })();
    }
  }, [isAuthenticated, isLoading, bootstrapUser]);

  return children;
}
